/**
 * A formal, printable Certificate of Cremation / Burial issued by the provider.
 * Shared by the provider journey modal and the owner tracker. Burial offerings
 * reframe the wording (no "ashes"/"cremated" language). The `.cert-print-region`
 * class + the global print rules isolate this card to a clean one-page document.
 */
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { is_burial, type CremationCertificate } from '@/features/cremation-fulfillment/types';

const SPECIES_LABEL: Record<string, string> = {
  DOG: 'dog',
  CAT: 'cat',
  BIRD: 'bird',
  RABBIT: 'rabbit',
  FISH: 'fish',
  OTHER: 'companion',
};

export function CremationCertificateCard({
  cert,
  rootTestId,
  nameTestId,
  numberTestId,
  printTestId,
}: {
  cert: CremationCertificate;
  rootTestId: string;
  nameTestId?: string;
  numberTestId: string;
  printTestId: string;
}) {
  const burial = is_burial(cert.service_type);
  const title = burial ? 'Certificate of Burial' : 'Certificate of Cremation';
  const verb = burial ? 'laid to rest' : 'cremated';
  const species = cert.deceased_pet_species ? SPECIES_LABEL[cert.deceased_pet_species] ?? 'companion' : 'companion';
  const date = new Date(cert.cremated_at).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const location = [cert.provider_city, cert.provider_state].filter(Boolean).join(', ');

  return (
    <div
      className="cert-print-region relative overflow-hidden rounded-card border-2 border-primary/30 bg-card p-6 text-center shadow-clay-sm"
      data-testid={rootTestId}
    >
      {/* Decorative inner frame */}
      <div className="pointer-events-none absolute inset-2 rounded-xl border border-primary/20" />

      <div className="relative flex flex-col items-center gap-1">
        {cert.provider_name && (
          <p className="font-heading text-sm font-bold tracking-wide text-foreground" data-testid={`${rootTestId}-issuer`}>
            {cert.provider_name}
          </p>
        )}
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-primary">{title}</p>

        <div className="my-2 h-px w-16 bg-primary/30" />

        <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
          This certifies that
        </p>
        <p className="font-heading text-2xl font-bold text-foreground" data-testid={nameTestId}>
          {cert.deceased_pet_name ?? 'a beloved companion'}
        </p>
        <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
          a cherished {species}
          {cert.deceased_pet_weight_kg ? ` · ${cert.deceased_pet_weight_kg} kg` : ''}, was {verb} with dignity and
          care on <span className="font-semibold text-foreground">{date}</span>.
        </p>

        <div className="my-3 h-px w-16 bg-primary/30" />

        <p className="font-mono text-sm font-semibold tabular-nums text-foreground" data-testid={numberTestId}>
          {cert.certificate_number}
        </p>
        <p className="text-xs text-muted-foreground">
          Issued by {cert.provider_name ?? 'your provider'}
          {location ? ` · ${location}` : ''}
        </p>

        <div className="cert-print-hide mt-4">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid={printTestId}>
            <Printer className="h-4 w-4" /> Download / print
          </Button>
        </div>
      </div>
    </div>
  );
}
